import React, { ReactElement } from 'react';
import styled from 'styled-components';
import UserCard from '../../UserCard';
import Badge from '../../Badge';
import { contentBasePadding } from '../BuilderInfoUI';
import { globalFormat } from '../../../utils/formatting';
import {
  ICreatorFieldsFragment,
  IEditorFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import useBrowserTimezoneLabel from '../../../utils/hooks/useBrowserTimezoneLabel';
import { theme } from '../../../styles/theme';

interface IProps {
  name: string;
  creator?: ICreatorFieldsFragment;
  editor?: IEditorFieldsFragment;
  createdAt?: string;
  editedAt?: string;
  description?: string | null;
  comment?: string | null;
  link?: ReactElement | null;
}

const Header = styled.div`
  ${contentBasePadding};
`;

const NameContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Name = styled.div`
  overflow-x: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: ${theme.colors.secondary900};
`;

const Description = styled.div`
  word-break: break-word;
  font-weight: 500;
`;

const Comment = styled.div`
  margin-top: 4px;
  font-weight: 500;
`;

const CreatorRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
  column-gap: 6px;
`;

const EditorRow = styled(CreatorRow)`
  margin-top: 2px;
`;

export default ({
  name,
  description,
  creator,
  editor,
  createdAt,
  editedAt,
  comment,
  link,
}: IProps) => {
  // use browser time zone in entity creation time
  const browserTimeZoneLabel = useBrowserTimezoneLabel();
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  return (
    <Header>
      <NameContainer>
        <Name>{name}</Name>
        {link}
      </NameContainer>
      {description && <Description>{description}</Description>}
      {creator && (editor ? editor.id !== creator.id : true) && (
        <CreatorRow>
          <UserCard
            user={creator}
            size="small"
            label={
              createdAt
                ? `Created ${globalFormat.formatDate(createdAt, {
                    dateFormat,
                    timeFormat,
                  })}`
                : ''
            }
          />
          <Badge color="grey" size="secondary">
            {browserTimeZoneLabel}
          </Badge>
        </CreatorRow>
      )}
      {editor && editedAt && (
        <EditorRow>
          <UserCard
            user={editor}
            size="small"
            label={`Last edited ${globalFormat.formatDate(editedAt, {
              dateFormat,
              timeFormat,
            })}`}
          />
          <Badge color="grey" size="secondary">
            {browserTimeZoneLabel}
          </Badge>
        </EditorRow>
      )}
      {comment && <Comment>{comment}</Comment>}
    </Header>
  );
};
